/**
 * The three derived tables of the TO Appointments page — Matrix 1 + 2, Matrix 3 and the
 * Tech Table summary — computed from the day the server sent.
 *
 * **Derived live, never stored** (03-appointments.md Part C invariant 1, §A5). The mockup
 * derives every matrix from its fixture array on every edit precisely so a filter can never
 * reshape a total wrongly and a stale count can never survive a change; this module is that
 * rule in TypeScript. Nothing here reads a count the server sent — it reads the appointments
 * the server sent and counts them, which is the same arithmetic the engine does and the only
 * way the four panels on this screen can agree with each other between two responses.
 *
 * The one figure that is NOT re-derived is the server's `balanced` verdict (§A4 rule 4): the
 * tolerance is the tournament's configuration and the server holds it, so the pill prints
 * what the server decided and this file never second-guesses it.
 *
 * Every function here is pure. That is the point — the panels are then only drawing.
 */

import type { AppointmentFixtureBody, AppointmentsViewBody, OfficialView } from "../../api/types.js";

/* ── Filters ──────────────────────────────────────────────────────────────── */

/** The competition tab of any one panel. Panels filter independently (Part C invariant 2). */
export type CompetitionFilter = "all" | "m" | "w";

/** The Fixture List's stage tab. */
export type StageFilter = "all" | "pool" | "post";

/** The officials tab shared by the three derived panels. */
export type RoleFilter = "all" | "TO" | "Judge";

export function competitionLabel(filter: CompetitionFilter): string {
  return filter === "m" ? "Men's" : filter === "w" ? "Women's" : "All Competitions";
}

/**
 * Whether a fixture belongs to the pool stage.
 *
 * The API sends the stage as TMS words it — `Pool A`, `Pool B`, and for the knockouts
 * whatever TMS calls them. Anything that does not begin `Pool` is post-pool, which is the
 * distinction the tab bar offers and the only one it needs.
 */
export function isPoolStage(fixture: AppointmentFixtureBody): boolean {
  return fixture.stage.trim().toLowerCase().startsWith("pool");
}

export function filterFixtures(
  fixtures: readonly AppointmentFixtureBody[],
  competition: CompetitionFilter,
  stage: StageFilter = "all",
): AppointmentFixtureBody[] {
  return fixtures.filter((fixture) => {
    if (competition !== "all" && fixture.competitionGender !== competition) return false;
    if (stage === "pool") return isPoolStage(fixture);
    if (stage === "post") return !isPoolStage(fixture);
    return true;
  });
}

/* ── Who the derived tables have rows for ─────────────────────────────────── */

/**
 * One official as the three derived tables name him: the surname the matrices head their
 * columns with, plus the country and the gender the type colours are taken from.
 *
 * Where the roster could not be loaded the appointments still carry a name and a country, so
 * a row is built from the appointment itself rather than dropped — a matrix missing the
 * official the TD is looking at would be worse than one missing his gender colour.
 */
export interface MatrixOfficial {
  tmsPersonId: string;
  /** Rendered in the matrix column heads and the row stubs. */
  surname: string;
  /** The full name, for the `title` on a surname column and for the Tech Table. */
  mediaName: string;
  country: string;
  role: string;
  gender: "m" | "f" | null;
}

/** The roles that may sit at the technical table (§A1's "old method"; §A4 rule 3 for ATDs). */
function isTechnicalRole(role: string): boolean {
  return role === "TO" || role === "Judge" || role === "ATD";
}

/**
 * Surname-first, as a media name is written: `OKONKWO Ada` → `OKONKWO`.
 *
 * Only used where the roster is absent and the appointment's `mediaName` is all there is.
 * With the roster loaded the surname comes from `surnames`, which is the field that means it.
 */
export function surnameOf(mediaName: string): string {
  const [first] = mediaName.trim().split(/\s+/);
  return first ?? mediaName;
}

/**
 * Everybody the derived tables should carry a row for, in a stable order.
 *
 * The pool first — every technical official on the roster, so an official with no
 * appointment yet is visibly at zero rather than absent, which is how the TD sees who is
 * free. Then anybody appointed in this scope whom the roster does not carry, appended rather
 * than dropped.
 */
export function matrixOfficials(
  view: AppointmentsViewBody,
  officials: readonly OfficialView[],
  role: RoleFilter = "all",
): MatrixOfficial[] {
  const rows: MatrixOfficial[] = [];
  const seen = new Set<string>();

  for (const official of officials) {
    if (!isTechnicalRole(official.role)) continue;
    seen.add(official.tmsPersonId);
    rows.push({
      tmsPersonId: official.tmsPersonId,
      surname: official.surnames.toUpperCase(),
      mediaName: official.mediaName,
      country: official.country,
      role: official.role,
      gender: official.gender,
    });
  }

  for (const fixture of view.fixtures) {
    for (const slot of fixture.slots) {
      if (slot.tmsPersonId === null || seen.has(slot.tmsPersonId)) continue;
      seen.add(slot.tmsPersonId);
      rows.push({
        tmsPersonId: slot.tmsPersonId,
        surname: surnameOf(slot.mediaName ?? slot.tmsPersonId),
        mediaName: slot.mediaName ?? slot.tmsPersonId,
        country: slot.country ?? "—",
        role: slot.role ?? "Judge",
        gender: null,
      });
    }
  }

  return rows.filter((entry) => role === "all" || entry.role === role);
}

/* ── Matrix 1 + 2 — appointments by role, and who worked with whom ────────── */

export interface MatrixRow {
  official: MatrixOfficial;
  /** `[TO, SJ, TJ]`, in the order the columns run. */
  roles: [number, number, number];
  roleTotal: number;
  /** Co-appointments, keyed by the other official's TMS person id. */
  co: Record<string, number>;
  coTotal: number;
}

export interface MatrixTable {
  rows: MatrixRow[];
  totals: { to: number; sj: number; tj: number; roleTotal: number; coTotal: number };
  /** Column sums of the co-appointment block, keyed by person id. */
  coColumnTotals: Record<string, number>;
  /** How many fixtures the table currently reflects — the `#matScope` sentence's number. */
  fixtures: number;
}

export function buildMatrix(
  fixtures: readonly AppointmentFixtureBody[],
  officials: readonly MatrixOfficial[],
): MatrixTable {
  const index = new Map(officials.map((official, position) => [official.tmsPersonId, position]));
  const rows: MatrixRow[] = officials.map((official) => ({
    official,
    roles: [0, 0, 0],
    roleTotal: 0,
    co: {},
    coTotal: 0,
  }));

  for (const fixture of fixtures) {
    const present: string[] = [];
    for (const slot of fixture.slots) {
      if (slot.tmsPersonId === null) continue;
      present.push(slot.tmsPersonId);
      const position = index.get(slot.tmsPersonId);
      if (position === undefined) continue;
      const row = rows[position];
      if (row === undefined) continue;
      const column = slot.slot === "TO" ? 0 : slot.slot === "SJ" ? 1 : 2;
      row.roles[column] += 1;
    }
    for (const one of present) {
      for (const other of present) {
        if (one === other) continue;
        const position = index.get(one);
        if (position === undefined) continue;
        const row = rows[position];
        if (row === undefined) continue;
        row.co[other] = (row.co[other] ?? 0) + 1;
      }
    }
  }

  const totals = { to: 0, sj: 0, tj: 0, roleTotal: 0, coTotal: 0 };
  const coColumnTotals: Record<string, number> = {};
  for (const official of officials) coColumnTotals[official.tmsPersonId] = 0;

  for (const row of rows) {
    row.roleTotal = row.roles[0] + row.roles[1] + row.roles[2];
    totals.to += row.roles[0];
    totals.sj += row.roles[1];
    totals.tj += row.roles[2];
    totals.roleTotal += row.roleTotal;
    let coTotal = 0;
    for (const official of officials) {
      if (official.tmsPersonId === row.official.tmsPersonId) continue;
      const value = row.co[official.tmsPersonId] ?? 0;
      coTotal += value;
      coColumnTotals[official.tmsPersonId] = (coColumnTotals[official.tmsPersonId] ?? 0) + value;
    }
    row.coTotal = coTotal;
    totals.coTotal += coTotal;
  }

  return { rows, totals, coColumnTotals, fixtures: fixtures.length };
}

/* ── Matrix 3 — Teams Coverage ────────────────────────────────────────────── */

export interface TeamsRow {
  official: MatrixOfficial;
  /** Matches involving each team code. */
  per: Record<string, number>;
  total: number;
}

export interface TeamsTable {
  /** Every team code in scope, alphabetically — the column order (§A5 Matrix 3). */
  teams: string[];
  rows: TeamsRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
  fixtures: number;
}

export function buildTeams(
  fixtures: readonly AppointmentFixtureBody[],
  officials: readonly MatrixOfficial[],
): TeamsTable {
  const index = new Map(officials.map((official, position) => [official.tmsPersonId, position]));
  const teamSet = new Set<string>();
  const rows: TeamsRow[] = officials.map((official) => ({ official, per: {}, total: 0 }));

  for (const fixture of fixtures) {
    const teams = [fixture.homeTeamCode, fixture.awayTeamCode].filter(
      (team): team is string => team !== null,
    );
    for (const team of teams) teamSet.add(team);
    for (const slot of fixture.slots) {
      if (slot.tmsPersonId === null) continue;
      const position = index.get(slot.tmsPersonId);
      if (position === undefined) continue;
      const row = rows[position];
      if (row === undefined) continue;
      for (const team of teams) {
        row.per[team] = (row.per[team] ?? 0) + 1;
        row.total += 1;
      }
    }
  }

  const teams = [...teamSet].sort();
  const columnTotals: Record<string, number> = {};
  let grandTotal = 0;
  for (const team of teams) {
    let sum = 0;
    for (const row of rows) sum += row.per[team] ?? 0;
    columnTotals[team] = sum;
    grandTotal += sum;
  }

  return { teams, rows, columnTotals, grandTotal, fixtures: fixtures.length };
}

/* ── Matrix 4 — the Tech Table summary ────────────────────────────────────── */

export interface TechRow {
  official: MatrixOfficial;
  to: number;
  sj: number;
  tj: number;
  total: number;
  /** `|SJ − TJ|`, the quantity §A4 rule 4 measures. Folded in from the old Workload panel. */
  difference: number;
}

/** Heaviest first, so the imbalance the TD is looking for is at the top (§A5 Matrix 4). */
export function buildTech(
  fixtures: readonly AppointmentFixtureBody[],
  officials: readonly MatrixOfficial[],
): TechRow[] {
  const matrix = buildMatrix(fixtures, officials);
  return matrix.rows
    .map((row) => ({
      official: row.official,
      to: row.roles[0],
      sj: row.roles[1],
      tj: row.roles[2],
      total: row.roleTotal,
      difference: Math.abs(row.roles[1] - row.roles[2]),
    }))
    .sort(
      (a, b) =>
        b.total - a.total ||
        b.difference - a.difference ||
        (a.official.surname < b.official.surname
          ? -1
          : a.official.surname > b.official.surname
            ? 1
            : 0),
    );
}

/* ── Heat ─────────────────────────────────────────────────────────────────── */

/** §A5's thresholds, verbatim: `n≥3 → h3` · `n===2 → h2` · `n===1 → h1` · else `c-zero`. */
export function heatClass(value: number): string {
  if (value >= 3) return "h3";
  if (value === 2) return "h2";
  if (value === 1) return "h1";
  return "c-zero";
}

/* ── The flash keys ───────────────────────────────────────────────────────── */

/**
 * Every derived cell on the screen, as `key → value`.
 *
 * This is what the change flash compares between renders (§A5: *"Changed cells flash"*). It
 * is built from the SAME tables the panels draw, so a cell that flashes is a cell whose
 * printed value genuinely moved — a flash driven by a separate calculation would eventually
 * light up cells that had not changed, which is worse than no flash at all.
 *
 * The keys are namespaced per panel because the same official appears in all three, and a
 * change in the matrix should light the matrix cell rather than a Teams Coverage cell that
 * happens to hold the same number.
 */
export function derivedCellValues(
  matrix: MatrixTable,
  tech: readonly TechRow[],
  teams: TeamsTable,
): Map<string, number> {
  const values = new Map<string, number>();

  for (const row of matrix.rows) {
    const id = row.official.tmsPersonId;
    values.set(`m:${id}:TO`, row.roles[0]);
    values.set(`m:${id}:SJ`, row.roles[1]);
    values.set(`m:${id}:TJ`, row.roles[2]);
    values.set(`m:${id}:rt`, row.roleTotal);
    values.set(`m:${id}:cot`, row.coTotal);
    for (const other of matrix.rows) {
      if (other.official.tmsPersonId === id) continue;
      values.set(`m:${id}:${other.official.tmsPersonId}`, row.co[other.official.tmsPersonId] ?? 0);
    }
  }

  for (const row of tech) {
    const id = row.official.tmsPersonId;
    values.set(`t:${id}:TO`, row.to);
    values.set(`t:${id}:SJ`, row.sj);
    values.set(`t:${id}:TJ`, row.tj);
    values.set(`t:${id}:tot`, row.total);
    values.set(`t:${id}:dif`, row.difference);
  }

  for (const row of teams.rows) {
    const id = row.official.tmsPersonId;
    for (const team of teams.teams) values.set(`f:${id}:${team}`, row.per[team] ?? 0);
    values.set(`f:${id}:tot`, row.total);
  }

  return values;
}

/* ── What the one-press stage draft is called ─────────────────────────────── */

/**
 * The two pool values of `fixture_stage`. Mirrored from `POOL_STAGES` in
 * `apps/api/src/routes/appointments-stage.ts` for the reason every wire shape in
 * `api/types.ts` is mirrored: the browser bundle must not pull the Fastify project's type
 * graph into itself. The API remains the authority — it decides the scope, and its reply
 * carries the label back — this only lets the button be named correctly *before* it is
 * pressed.
 */
const POOL_STAGES: readonly string[] = ["Pool A", "Pool B"];

/**
 * What the stage-draft button says, worked out from the schedule.
 *
 * When the fixture rows name pool matches, the button says what it does: `Draft The Pool
 * Stage`. When nothing in the schedule is marked as a pool match, the data cannot tell a
 * stage from a knockout, so the button says the honest, wider thing instead —
 * `Draft All Remaining Days` — rather than promising a scope the data does not support.
 */
export function stageDraftLabel(fixtures: readonly { stage: string }[]): string {
  return fixtures.some((fixture) => POOL_STAGES.includes(fixture.stage))
    ? "Draft The Pool Stage"
    : "Draft All Remaining Days";
}
